import { Box, IconButton, Typography, useTheme, SxProps, Theme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { useCodingDisplay } from 'app/hooks/useCodingDisplay';
import { sentryMaskIf } from 'sentryReplayPrivacy';

import {
  FlexGlCode,
  FlexCostCode,
  FlexCostType,
  FlexCode as FlexProject,
  FlexVendor,
  AgaveGlCode,
  AgaveProject,
  AgaveCostCode,
  AgaveCostType,
  AgaveSubJob,
  AgaveVendor,
  AgaveLocation,
  AgaveDepartment,
  AgaveEmployeeEntity,
} from 'API';

type CodingFieldType =
  | FlexGlCode
  | FlexCostCode
  | FlexCostType
  | FlexProject
  | FlexVendor
  | AgaveGlCode
  | AgaveProject
  | AgaveCostCode
  | AgaveCostType
  | AgaveVendor
  | AgaveSubJob
  | AgaveLocation
  | AgaveDepartment
  | AgaveEmployeeEntity;

interface GLAccountFieldProps {
  currentValue: CodingFieldType | null | undefined;
  onAdd: () => void;
  onClear?: () => void;
  title: string;
  sx?: SxProps<Theme>;
  selectedValue?: CodingFieldType | null;
  disabled?: boolean;
  /** SEE-1834: the value is PII (ERP employee name) — mask it in session replays. */
  maskValue?: boolean;
}

const MobileCodingField = ({
  currentValue,
  onAdd,
  onClear,
  title,
  sx,
  selectedValue,
  disabled,
  maskValue = false,
}: GLAccountFieldProps) => {
  const theme = useTheme();
  const { getDisplayName } = useCodingDisplay();

  // Determine if there's a value to display (and potentially clear)
  const hasValue = selectedValue || (selectedValue === undefined && currentValue);

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering onAdd
    if (onClear) {
      onClear();
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        '&:focus': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: '2px',
        },

        ...sx,
      }}
      onClick={disabled ? undefined : onAdd}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onAdd();
        }
      }}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={`Add ${title}`}
      aria-disabled={disabled}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
          {title}
        </Typography>
        <AddIcon sx={{ color: theme.palette.primary.main }} />
      </Box>

      {/* Show value box if:
          - selectedValue is explicitly set (truthy), OR
          - selectedValue is undefined (not yet modified) AND currentValue exists
          - Don't show if selectedValue is null (explicitly cleared) */}
      {hasValue && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 1.5,
            borderRadius: 2,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            width: 'fit-content',
            maxWidth: '100%',
            border: `1px solid ${theme.palette.primary.light}`,
            backgroundColor: theme.palette.primary.lighter,
          }}
        >
          {/* Show currentValue only if selectedValue is undefined (not yet modified) */}
          {currentValue && selectedValue === undefined && (
            <Typography
              variant="body1"
              sx={{ color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis' }}
              {...sentryMaskIf(maskValue)}
            >
              {getDisplayName(currentValue)}
            </Typography>
          )}
          {selectedValue && (
            <Typography
              variant="body1"
              sx={{ color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis' }}
              {...sentryMaskIf(maskValue)}
            >
              {getDisplayName(selectedValue)}
            </Typography>
          )}
          {/* Clear button */}
          {onClear && !disabled && (
            <IconButton
              size="small"
              onClick={handleClear}
              sx={{
                p: 0.5,
                ml: 0.5,
                color: 'text.secondary',
                '&:hover': {
                  color: 'error.main',
                  backgroundColor: 'error.lighter',
                },
              }}
              aria-label={`Clear ${title}`}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      )}
    </Box>
  );
};

export default MobileCodingField;
